import { memo, useContext, useMemo, useState } from "react";
import DataGrid, { EditorProps, FormatterProps, RowsChangeData } from 'react-data-grid'
import { AiOutlineReload } from 'react-icons/ai'
import { FaRegClock } from 'react-icons/fa'

import { Button, Alert } from '@aws-amplify/ui-react'

import { OrderReceiptColumns, SummaryReceiptRow } from "../../../constants/Column";
import { ceil2Decimal } from "../../../utilities/Format";
import { OrderReceipt, ProductionOrder } from "../../../components/types";
import OrderReceiptContext from "../../../contexts/OrderReceiptContext";
import PicklistContext from "../../../contexts/PicklistContext";
import {
  getOrderReceipts,
  updateIssueQtyOrderReceipt,
  updateOtherAndNoteOrderReceipt,
  updateRejectAndNoteOrderReceipt,
  updateTotalAmountEAOrderReceipt,
  deleteReceipt,
  updateBulkUsageOrderReceipt
} from "../../../states/PicklistAction";
import OrderContext from "../../../contexts/OrderContext";
import EmptyRow from "../../../components/EmptyRow";
import { calculateYieldFilling, calculateYieldPacking } from "../../../logic/yield";
import { MdOutlineCancel } from "react-icons/md";
import RemoveUnusedReceiptEditor from "../../../components/RemoveUnusedReceiptEditor";

const RetainRecord = ({
  isFilling,
  title = 'Order Receipt',
  disabledInput = false,
  orderFromParent,
  receiptsFromParent,
  setReceiptsToParent,
  setReceiptToParent,
  canDelete = false,
  canEditBulk = false
} : {
  isFilling?: boolean,
  title?: string,
  disabledInput?: boolean,
  orderFromParent?: ProductionOrder,
  receiptsFromParent?: OrderReceipt[],
  setReceiptsToParent?: (receipts: OrderReceipt[]) => void
  setReceiptToParent?: (receipt: OrderReceipt) => void,
  canDelete?: boolean,
  canEditBulk?: boolean
}) => {
  const orderReceiptContext = useContext(OrderReceiptContext)
  const orderContext = useContext(OrderContext)
  const picklistContext = useContext(PicklistContext)
  
  const order = orderFromParent || (orderContext.order as unknown as ProductionOrder)
  
  const orderIsFilling = useMemo(() => {
    const isFillingFromCtx = picklistContext ? picklistContext.items.filter(itm => itm.materialType === 'BULK').length > 0 : false
    return isFilling || isFillingFromCtx
  }, [ isFilling, picklistContext ])

  const mutateReceipts = (updatedReceipts: OrderReceipt[]) => {
    if (setReceiptsToParent) {
      setReceiptsToParent(updatedReceipts)
    } else {
      orderReceiptContext?.mutateOrderReceipts(updatedReceipts)
    }
  }

  const mutateOrderReceipt = (updatedReceipt: OrderReceipt) => {
    if (setReceiptToParent) {
      setReceiptToParent(updatedReceipt)
    } else {
      orderReceiptContext?.mutateOrderReceipt(updatedReceipt)
    }
  }

  const [ isReloading, setIsReloading ] = useState<boolean>(false)

  const orderReceiptRows = useMemo(() => 
    (receiptsFromParent || orderReceiptContext?.orderReceipts || [])
    .filter(row => !row._deleted)
    .map((row, idx, array) => {
      return {
        ...row,
        isFilling: orderIsFilling,
        yieldTolerance: orderIsFilling ? calculateYieldFilling(row) : calculateYieldPacking(array, order.orderQty),
      }
    })
  , [ orderIsFilling, receiptsFromParent, orderReceiptContext?.orderReceipts, order.orderQty ])

  const summaryReceipt = useMemo(() => {
    const receiptLogs = orderReceiptRows
    const totalAmount = receiptLogs.reduce((acc, row) => acc + row.totalAmount!, 0)
    const totalDz = receiptLogs.reduce((acc, row) => acc + Math.floor(row.totalAmount!/12), 0)
    const totalBulkUsed = receiptLogs.reduce((acc, row) => acc + row.bulkUsage!, 0)
    const totalRetained = receiptLogs.reduce((acc, row) => acc + row.retainedIssue!, 0)
    const totalMicroIssue = receiptLogs.reduce((acc, row) => acc + row.microIssue!, 0)
    const totalValidateIssue = receiptLogs.reduce((acc, row) => acc + row.validateIssue!, 0)
    const totalShelfLifeIssue = receiptLogs.reduce((acc, row) => acc + row.shelfLifeIssue!, 0)
    const totalTestIssue = receiptLogs.reduce((acc, row) => acc + row.testIssue!, 0)
    const totalOtherIssue = receiptLogs.reduce((acc, row) => acc + row.otherIssue!, 0)
    const totalReject = receiptLogs.reduce((acc, row) => acc + row.reject!, 0)
    
    const totalNetEa = receiptLogs.reduce((acc, row) => {
      const sumOfQCSample = (row.retainedIssue || 0) + (row.microIssue || 0) + 
        (row.validateIssue || 0) + (row.shelfLifeIssue || 0) + (row.testIssue || 0) + 
        (row.otherIssue || 0)
        const net = (row.totalAmount || 0) - (row.reject || 0) - sumOfQCSample
      return acc + net
    }, 0)

    const sumamry : SummaryReceiptRow = {
      dz: ceil2Decimal(totalDz),
      ea: ceil2Decimal(totalAmount),
      bulkUsed: ceil2Decimal(totalBulkUsed),
      retained: ceil2Decimal(totalRetained),
      micro: ceil2Decimal(totalMicroIssue),
      validate: ceil2Decimal(totalValidateIssue),
      shelfLife: ceil2Decimal(totalShelfLifeIssue),
      test: ceil2Decimal(totalTestIssue),
      other: ceil2Decimal(totalOtherIssue),
      reject: ceil2Decimal(totalReject),
      netEa: ceil2Decimal(totalNetEa),
      yield: receiptLogs.length > 0 && !receiptLogs[0].isFilling ? ceil2Decimal(receiptLogs[0].yieldTolerance || 0) : 0 // for yieldPacking case
    }
    return [ sumamry ]
  }, [ orderReceiptRows ])

  const onRowsChange = async (rows: OrderReceipt[], 
    changedInfo: RowsChangeData<OrderReceipt, SummaryReceiptRow>) => {

    const changedRowIdx = changedInfo.indexes[0]
    const updatedRow = rows[changedRowIdx]
    if (changedInfo.column.key === 'retainedIssue') {
      const updatedIssue = await updateIssueQtyOrderReceipt(
        updatedRow.id, 'retainedIssue', updatedRow.retainedIssue || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'microIssue') {
      const updatedIssue = await updateIssueQtyOrderReceipt(
        updatedRow.id, 'microIssue', updatedRow.microIssue || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'validateIssue') {
      const updatedIssue = await updateIssueQtyOrderReceipt(
        updatedRow.id, 'validateIssue', updatedRow.validateIssue || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'shelfLifeIssue') {
      const updatedIssue = await updateIssueQtyOrderReceipt(
        updatedRow.id, 'shelfLifeIssue', updatedRow.shelfLifeIssue || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'testIssue') {
      const updatedIssue = await updateIssueQtyOrderReceipt(
        updatedRow.id, 'testIssue', updatedRow.testIssue || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'otherIssue') {
      const updatedIssue = await updateOtherAndNoteOrderReceipt(
        updatedRow.id, updatedRow.otherIssue || 0, updatedRow.otherIssueNote || '', updatedRow._version || 1
      )
      mutateOrderReceipt(updatedIssue)
    } else if (changedInfo.column.key === 'reject') {
      const updatedReject = await updateRejectAndNoteOrderReceipt(
        updatedRow.id, updatedRow.reject || 0, updatedRow.remark || '', updatedRow._version || 1
      )
      mutateOrderReceipt(updatedReject)
    } else if (changedInfo.column.key === 'totalAmount') {
      const updatedTotalAmount = await updateTotalAmountEAOrderReceipt(
        updatedRow.id, updatedRow.totalAmount || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedTotalAmount)
    } else if (changedInfo.column.key === 'delete') {
      const deletedReceipt = await deleteReceipt(updatedRow.id, updatedRow._version || 1)
      mutateOrderReceipt(deletedReceipt)
    } else if (changedInfo.column.key === 'bulkUsage') {
      const updatedBulkUsage = await updateBulkUsageOrderReceipt(
        updatedRow.id, updatedRow.bulkUsage || 0, updatedRow._version || 1
      )
      mutateOrderReceipt(updatedBulkUsage)
    }
  }

  const reloadOrderReceipt = async () => {
    setIsReloading(true)

    const orderReceipts =  await getOrderReceipts(order.id)
    mutateReceipts(orderReceipts)

    setIsReloading(false)
  }

  const lastUpdated = window.localStorage.getItem('lastUpdatedReceipts') || ''

  const columns = useMemo(() => {
    let cols = Array.from(OrderReceiptColumns)
    if (canDelete) {
      cols.push({
        key: 'delete', name: '', minWidth: 50, width: 50,
        cellClass: `flex items-center justify-center cursor-pointer`,
        formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
          return <MdOutlineCancel color='red' size={20}/>
        },
        editable: !disabledInput,
        editor : (props: EditorProps<OrderReceipt, SummaryReceiptRow>) => {
          return <RemoveUnusedReceiptEditor props={props} />
        },
        editorOptions: {
          editOnClick: true
        }
      })
    }
    if (canEditBulk) {
      cols = cols.map(col => {
        if (col.key === 'bulkUsage') {
          return {
            ...col,
            editable: row => (row.bulkUsage || 0) > 0 && (row.approvedByWhDatetime || 0) <= 0 && !disabledInput,
          }
        }
        return col
      })
    }
    return cols.map(col => ({ 
      ...col,
      editable: col.key === 'yieldTolerance' || col.key === 'bulkUsage' ? col.editable : (row: OrderReceipt) => (row.approvedByWhDatetime || 0) <= 0 && !disabledInput
    }))
  }, [ disabledInput, canDelete, canEditBulk ])

  const isShowMessageForEditEa = useMemo(() => {
    const materialDescription = order.materialDescription || ''
    // check if the material description consist of 'PK2', 'TWIN', 'PK3+1', '3+1', '6+2'
    return materialDescription.includes('PK2') || materialDescription.includes('TWIN') || 
      materialDescription.includes('PK3+1') || materialDescription.includes('3+1') || 
      materialDescription.includes('6+2')
  }, [ order ])
  return (
    <div className='my-3 space-y-5'>
      <div className="flex items-center justify-between ">
        <div className="space-y-1 ">
          <div className="flex gap-3 h-fit">
            <p className="text-2xl font-bold ">{title}</p>
            <Button
              isLoading={isReloading}
              loadingText="Loading..."
              size='small'
              onClick={reloadOrderReceipt}
              >
              <AiOutlineReload />
            </Button>
          </div>
          <p className="text-xs ">อัพเดตล่าสุด : {lastUpdated}</p>
        </div>
        {isShowMessageForEditEa && <Alert
          variation="warning"
          isDismissible={true}
          hasIcon={false}
          >
          แก้ยอด <span className="font-bold underline">ผลิตได้</span> เป็นหน่วย<span className="font-bold underline ">ย่อย EA ที่ผลิตได้จริงทั้งหมด</span>
        </Alert>}
        <div className='flex flex-col items-end'>
          <small className="flex items-center gap-1 font-light"><FaRegClock />อายุสินค้า</small>
          <label className="text-3xl font-bold">{order.shelfLifeMonth ? order.shelfLifeMonth : (Number(order.shelfLife) || 0) * 12} เดือน</label>
        </div>
      </div>
      <div>
        {orderReceiptRows.length > 0 ? <DataGrid
          style={{ height: 'fit-content' }}
          columns={columns}
          rows={orderReceiptRows}
          defaultColumnOptions={{ resizable: true }}
          summaryRows={summaryReceipt}
          onRowsChange={onRowsChange}
          summaryRowHeight={50}
        /> : <EmptyRow message="ยังไม่พบการบันทึกการผลิต" />
        }
      </div>
    </div>
  );
}

export default memo(RetainRecord);